import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  MdOutlineAdd,
  MdOutlineAutorenew,
  MdOutlineRemove,
} from "react-icons/md";
import axios from "../../config/axiosInstance";
import { SUBSCRIPTIONS } from "../../config/apiConfig";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import Modal from "../modals/Modal";
import ClientPicker from "../crm/ClientPicker";
import { ErrorBlock, SkeletonRows } from "../crm/Primitives";
import { formatDate, formatMoney } from "../crm/crmHelpers";
import { parseMoneyInput, toMoneyInput } from "../events/eventHelpers";

/*
  Criar e editar a assinatura de um salão.

  Toda cobrança é mensal: é o que faz a assinatura não comprometer o limite do cartão.
  O valor de tabela é a mensalidade normal em qualquer ciclo, e o tempo de assinatura
  define quantas cobranças saem antes de ela parar sozinha. Escolher um ciclo só preenche
  o tempo dele (semestral, 6 meses; mensal, 12), e a equipe pode combinar outro. Sem prazo,
  ela vai até alguém cancelar.

  A tabela de valores vem do servidor (`/subscriptions/options`), com a mesma conta que a
  cobrança usa, e nada de preço é calculado aqui. Por cima dela a equipe pode negociar
  outro valor: o servidor guarda os dois, para a ficha mostrar que foi negociado e quanto
  seria na tabela.

  Na edição, o que muda vale para as próximas cobranças e para as que já foram geradas e
  seguem em aberto. O plano da conta só muda quando o próximo pagamento entrar, a não ser
  que a equipe peça para aplicar agora.
*/

const MAX_USERS = 200;
const DEFAULT_PLAN = "PRO";
const DEFAULT_CYCLE = "MONTHLY";
const DURATION_PRESETS = [3, 6, 12, 18, 24, 36];
const MAX_DURATION_MONTHS = 120;
// Prazo da assinatura no ciclo mensal. Espelho de config/planCatalog.js no servidor.
const MONTHLY_TERM_MONTHS = 12;
const defaultDurationOf = (cycleMonths) =>
  String(cycleMonths > 1 ? cycleMonths : MONTHLY_TERM_MONTHS);
const OTHER_DURATION = "other";

const labelClass =
  "mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400";
const fieldClass =
  "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100 disabled:bg-slate-50 disabled:text-slate-400";

const BILLING_HINTS = {
  CREDIT_CARD:
    "A cliente informa o cartão na fatura da primeira cobrança. Depois, as cobranças saem sozinhas todo mês, sem comprometer o limite: cada uma usa só o valor do mês.",
  PIX: "Todo mês a cliente recebe o link por e-mail e paga pelo QR Code.",
  BOLETO:
    "Todo mês a cliente recebe o link por e-mail. O boleto pode levar até 3 dias úteis para compensar.",
};

// Quando sai a última cobrança, com a mesma conta do servidor (31/01 + 1 mês = 28/02).
const lastChargeOf = (firstDueDate, months) => {
  if (!firstDueDate || !months) return null;
  const [year, month, day] = firstDueDate.split("-").map(Number);
  const target = new Date(year, month - 1 + months - 1, 1, 12);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(day, lastDay));
  const pad = (value) => String(value).padStart(2, "0");
  return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}`;
};

const chipClass = (active) =>
  `rounded-xl border px-3 py-2 text-left text-sm font-semibold transition ${
    active
      ? "border-pink-500 bg-pink-50 text-pink-700 ring-1 ring-pink-200"
      : "border-slate-200 bg-white text-slate-700 hover:border-pink-200"
  }`;

const SubscriptionFormModal = ({ company, subscription, onClose, onSaved }) => {
  const isEditing = Boolean(subscription);

  const [picked, setPicked] = useState(null);
  const target = company || picked;
  const companyId = target?.id;

  const [usersText, setUsersText] = useState(
    subscription ? String(subscription.numberOfUsers) : ""
  );
  const [planId, setPlanId] = useState(subscription?.planId || DEFAULT_PLAN);
  const [cycleId, setCycleId] = useState(subscription?.billingCycle || DEFAULT_CYCLE);
  const [billingType, setBillingType] = useState(subscription?.billingType || "CREDIT_CARD");
  const [customValue, setCustomValue] = useState(Boolean(subscription?.isNegotiatedValue));
  const [valueText, setValueText] = useState(
    subscription?.isNegotiatedValue ? toMoneyInput(subscription.value) : ""
  );
  const [dueDate, setDueDate] = useState(subscription?.nextDueDate || "");
  const [durationChoice, setDurationChoice] = useState(() => {
    if (!subscription) return defaultDurationOf(1);
    return subscription.durationMonths ? String(subscription.durationMonths) : "";
  });
  const [customDuration, setCustomDuration] = useState("");
  const [documentId, setDocumentId] = useState("");
  const [note, setNote] = useState(subscription?.note || "");
  const [applyNow, setApplyNow] = useState(false);
  const [error, setError] = useState(null);

  const parsedUsers = Number(usersText);
  const users =
    usersText.trim() === ""
      ? undefined
      : Number.isInteger(parsedUsers) && parsedUsers >= 1 && parsedUsers <= MAX_USERS
      ? parsedUsers
      : null;
  const debouncedUsers = useDebouncedValue(users, 300);

  const options = useQuery(
    ["subscriptions", "options", companyId, debouncedUsers],
    async () => {
      const params = new URLSearchParams({ companyId });
      if (debouncedUsers) params.set("users", debouncedUsers);
      const { data } = await axios.get(`${SUBSCRIPTIONS.OPTIONS}?${params.toString()}`);
      return data.data;
    },
    {
      enabled: Boolean(companyId) && debouncedUsers !== null,
      cacheTime: 0,
      refetchInterval: false,
      keepPreviousData: true,
    }
  );

  const info = options.data;

  // Trocar de cliente recomeça a equipe e a data: as da conta anterior não valem.
  useEffect(() => {
    if (isEditing) return;
    setUsersText("");
    setDueDate("");
  }, [companyId, isEditing]);

  // A equipe e a data partem do que a conta tem hoje. A resposta anterior continua
  // na tela enquanto a nova não chega, e não pode preencher a conta errada.
  useEffect(() => {
    if (!info || info.company.id !== Number(companyId)) return;
    setUsersText((current) => current || String(info.company.numberOfUsers));
    setDueDate((current) => current || info.defaultFirstDueDate);
  }, [info, companyId]);

  const pricing =
    users === null || (users !== undefined && users !== debouncedUsers) || options.isFetching;

  const plans = info?.plans || [];
  const selectedPlan = plans.find((plan) => plan.id === planId) || plans[0];
  const cycles = selectedPlan?.cycles || [];
  const selectedCycle = cycles.find((cycle) => cycle.id === cycleId) || cycles[0];

  // O ciclo traz o tempo dele. Quem combinou outro escolhe logo depois.
  const selectCycle = (cycle) => {
    setCycleId(cycle.id);
    setDurationChoice(defaultDurationOf(cycle.months));
  };

  const isOther = durationChoice === OTHER_DURATION;
  const durationMonths = isOther
    ? customDuration.trim()
      ? Number(customDuration)
      : null
    : durationChoice
    ? Number(durationChoice)
    : null;
  const durationInvalid =
    durationMonths !== null &&
    (!Number.isInteger(durationMonths) ||
      durationMonths < 1 ||
      durationMonths > MAX_DURATION_MONTHS);
  // Na edição a contagem segue de onde a série já está, e não da data da próxima cobrança.
  const lastCharge = durationInvalid
    ? null
    : lastChargeOf(
        isEditing && subscription.lastPaidAt ? subscription.firstDueDate : dueDate,
        durationMonths
      );

  const negotiated = customValue ? parseMoneyInput(valueText) : null;
  const valueInvalid = customValue && !(negotiated > 0);
  const finalValue = customValue ? negotiated : selectedCycle?.monthlyAmount;

  const needsDocument = !isEditing && info && !info.company.hasDocument;
  const documentDigits = documentId.replace(/\D/g, "");
  const documentInvalid =
    needsDocument && documentDigits.length !== 11 && documentDigits.length !== 14;

  const overlapsPaidPeriod =
    info &&
    !isEditing &&
    dueDate &&
    info.defaultFirstDueDate !== info.today &&
    dueDate < info.defaultFirstDueDate;

  const canApplyNow = isEditing && Boolean(subscription.lastPaidAt);
  const planDiffers =
    isEditing &&
    (planId !== subscription.planId || (users ?? subscription.numberOfUsers) !== subscription.numberOfUsers);

  const save = useMutation(
    () => {
      if (isEditing) {
        return axios.put(`${SUBSCRIPTIONS.ONE}/${subscription.id}`, {
          planId,
          billingCycle: selectedCycle.id,
          numberOfUsers: users,
          billingType,
          // `null` volta para o valor de tabela.
          value: customValue ? negotiated : null,
          durationMonths,
          ...(dueDate && subscription.nextDueDate && dueDate !== subscription.nextDueDate
            ? { nextDueDate: dueDate }
            : {}),
          note,
          applyNow: canApplyNow && applyNow,
        });
      }

      return axios.post(SUBSCRIPTIONS.CREATE, {
        companyId,
        planId,
        billingCycle: selectedCycle.id,
        numberOfUsers: users,
        billingType,
        ...(customValue ? { value: negotiated } : {}),
        firstDueDate: dueDate,
        durationMonths,
        ...(needsDocument ? { documentId: documentDigits } : {}),
        note,
      });
    },
    {
      onSuccess: ({ data }) => {
        if (isEditing) onSaved?.(data.data);
        else onSaved?.(data.data.subscription, data.data.company);
      },
      onError: (err) =>
        setError(
          err.response?.data?.message || "Não foi possível salvar a assinatura."
        ),
    }
  );

  const blocked =
    !target ||
    !selectedCycle ||
    pricing ||
    save.isLoading ||
    valueInvalid ||
    durationInvalid ||
    documentInvalid ||
    !dueDate;

  return (
    <Modal
      isOpen
      onClose={() => !save.isLoading && onClose()}
      size="lg"
      icon={MdOutlineAutorenew}
      title={isEditing ? "Editar assinatura" : "Nova assinatura"}
      subtitle={target?.name || "Cobrança recorrente no Asaas"}
      closeOnBackdrop={!save.isLoading}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          {selectedCycle ? (
            <p className="text-xs text-slate-500 sm:mr-auto">
              <span className="font-bold text-slate-900">
                {finalValue > 0 ? formatMoney(finalValue) : "—"}
              </span>{" "}
              por mês
              {durationMonths && !durationInvalid && finalValue > 0
                ? `, ${durationMonths} ${durationMonths === 1 ? "cobrança" : "cobranças"} = ${formatMoney(
                    finalValue * durationMonths
                  )}`
                : ""}
            </p>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            disabled={save.isLoading}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
          >
            Voltar
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            disabled={blocked}
            className="rounded-xl bg-pink-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-pink-700 disabled:opacity-40"
          >
            {save.isLoading
              ? "Salvando..."
              : isEditing
              ? "Salvar alterações"
              : "Criar assinatura"}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {error ? (
          <p
            role="alert"
            className="rounded-xl bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-800 ring-1 ring-inset ring-rose-200"
          >
            {error}
          </p>
        ) : null}

        {!company ? (
          <div>
            <p className={labelClass}>Cliente</p>
            <ClientPicker value={picked} onChange={setPicked} disabled={save.isLoading} />
          </div>
        ) : null}

        {!target ? (
          <p className="rounded-xl bg-slate-50 px-3 py-6 text-center text-xs text-slate-500">
            Escolha o cliente para ver produtos e valores.
          </p>
        ) : options.isLoading ? (
          <SkeletonRows rows={5} />
        ) : options.error ? (
          <ErrorBlock
            message={
              options.error.response?.data?.message ||
              "Não foi possível carregar as opções."
            }
            onRetry={options.refetch}
          />
        ) : (
          <>
            {isEditing && subscription.couponCode ? (
              <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 ring-1 ring-inset ring-amber-200">
                Esta assinatura tem o cupom {subscription.couponCode}. Mudar valor, produto, ciclo
                ou forma de pagamento antes do primeiro pagamento remove o desconto.
              </p>
            ) : null}

            <fieldset>
              <legend className={labelClass}>Produto</legend>
              <div className="grid grid-cols-3 gap-2" role="radiogroup">
                {plans.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    role="radio"
                    aria-checked={plan.id === selectedPlan?.id}
                    onClick={() => setPlanId(plan.id)}
                    className={chipClass(plan.id === selectedPlan?.id)}
                  >
                    {plan.name}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset>
              <legend className={labelClass}>Profissionais</legend>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Menos um profissional"
                  disabled={(users ?? 1) <= 1}
                  onClick={() => setUsersText(String(Math.max((users ?? 2) - 1, 1)))}
                  className="rounded-xl border border-slate-200 p-2.5 text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
                >
                  <MdOutlineRemove size={18} />
                </button>
                <input
                  inputMode="numeric"
                  aria-label="Quantidade de profissionais"
                  value={usersText}
                  onChange={(event) =>
                    setUsersText(event.target.value.replace(/\D/g, "").slice(0, 3))
                  }
                  className="w-20 rounded-xl border border-slate-200 px-3 py-2 text-center text-base font-bold text-slate-900 outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                />
                <button
                  type="button"
                  aria-label="Mais um profissional"
                  disabled={(users ?? 0) >= MAX_USERS}
                  onClick={() => setUsersText(String(Math.min((users ?? 0) + 1, MAX_USERS)))}
                  className="rounded-xl border border-slate-200 p-2.5 text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
                >
                  <MdOutlineAdd size={18} />
                </button>
              </div>
              {users === null ? (
                <p role="alert" className="mt-1 text-[11px] font-semibold text-rose-700">
                  Informe de 1 a {MAX_USERS} profissionais.
                </p>
              ) : selectedPlan?.id === "DIAMOND" ? (
                <p className="mt-1 text-[11px] text-slate-500">
                  No Diamond o valor é o mesmo para qualquer quantidade.
                </p>
              ) : (
                <p className="mt-1 text-[11px] text-slate-500">
                  A conta tem {info.company.activeProfessionals} profissional(is) ativo(s) hoje.
                </p>
              )}
            </fieldset>

            <fieldset>
              <legend className={labelClass}>Ciclo</legend>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="radiogroup">
                {cycles.map((cycle) => (
                  <button
                    key={cycle.id}
                    type="button"
                    role="radio"
                    aria-checked={cycle.id === selectedCycle?.id}
                    onClick={() => selectCycle(cycle)}
                    className={chipClass(cycle.id === selectedCycle?.id)}
                  >
                    {cycle.label}
                    <span className="mt-0.5 block text-[11px] font-medium text-slate-500">
                      {defaultDurationOf(cycle.months)} cobranças
                    </span>
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-slate-500">
                A cobrança é sempre mensal, pela mensalidade normal. O ciclo só define o tempo de assinatura.
              </p>
            </fieldset>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="subscription-duration" className={labelClass}>
                  Tempo de assinatura
                </label>
                <select
                  id="subscription-duration"
                  value={durationChoice}
                  onChange={(event) => setDurationChoice(event.target.value)}
                  className={fieldClass}
                >
                  <option value="">Sem prazo (até cancelar)</option>
                  {durationChoice &&
                  !isOther &&
                  !DURATION_PRESETS.includes(Number(durationChoice)) ? (
                    <option value={durationChoice}>
                      {durationChoice === "1" ? "1 mês" : `${durationChoice} meses`}
                    </option>
                  ) : null}
                  {DURATION_PRESETS.map((months) => (
                    <option key={months} value={String(months)}>
                      {months} meses
                    </option>
                  ))}
                  <option value={OTHER_DURATION}>Outro...</option>
                </select>
                {isOther ? (
                  <input
                    inputMode="numeric"
                    aria-label="Tempo de assinatura em meses"
                    placeholder="Meses"
                    value={customDuration}
                    onChange={(event) =>
                      setCustomDuration(event.target.value.replace(/\D/g, "").slice(0, 3))
                    }
                    className={`${fieldClass} mt-2`}
                  />
                ) : null}
                <p
                  role={durationInvalid ? "alert" : undefined}
                  className={`mt-1 text-[11px] ${
                    durationInvalid ? "font-semibold text-rose-700" : "text-slate-500"
                  }`}
                >
                  {durationInvalid
                    ? `Informe de 1 a ${MAX_DURATION_MONTHS} meses.`
                    : durationMonths
                    ? `${durationMonths} ${
                        durationMonths === 1 ? "cobrança mensal" : "cobranças mensais"
                      } e para sozinha${lastCharge ? `: a última em ${formatDate(lastCharge)}` : ""}.`
                    : "Cobra todo mês até alguém cancelar."}
                </p>
              </div>

              <div>
                <label htmlFor="subscription-due" className={labelClass}>
                  {isEditing && subscription.lastPaidAt
                    ? "Próxima cobrança"
                    : "Primeira cobrança"}
                </label>
                <input
                  id="subscription-due"
                  type="date"
                  min={info.today}
                  value={dueDate}
                  disabled={isEditing && !subscription.nextDueDate}
                  onChange={(event) => setDueDate(event.target.value)}
                  className={fieldClass}
                />
                {overlapsPaidPeriod ? (
                  <p className="mt-1 text-[11px] font-semibold text-amber-700">
                    A conta já pagou até {formatDate(info.company.planExpirationDate)}. Cobrar
                    antes disso faz ela pagar duas vezes pelos mesmos dias.
                  </p>
                ) : (
                  <p className="mt-1 text-[11px] text-slate-500">
                    A conta tem acesso até {formatDate(info.company.planExpirationDate)}.
                  </p>
                )}
              </div>
            </div>

            <fieldset>
              <legend className={labelClass}>Valor mensal</legend>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup">
                <button
                  type="button"
                  role="radio"
                  aria-checked={!customValue}
                  onClick={() => setCustomValue(false)}
                  className={chipClass(!customValue)}
                >
                  Valor de tabela
                  <span className="mt-0.5 block text-[11px] font-medium text-slate-500">
                    {selectedCycle ? `${formatMoney(selectedCycle.monthlyAmount)}/mês` : "—"}
                  </span>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={customValue}
                  onClick={() => setCustomValue(true)}
                  className={chipClass(customValue)}
                >
                  Valor negociado
                  <span className="mt-0.5 block text-[11px] font-medium text-slate-500">
                    Fica registrado junto com o de tabela
                  </span>
                </button>
              </div>
              {customValue ? (
                <input
                  inputMode="decimal"
                  aria-label="Valor negociado por mês"
                  placeholder="149,90"
                  value={valueText}
                  onChange={(event) => setValueText(event.target.value)}
                  className={`${fieldClass} mt-2`}
                />
              ) : null}
            </fieldset>

            <fieldset>
              <legend className={labelClass}>Forma de pagamento</legend>
              <div className="grid grid-cols-3 gap-2" role="radiogroup">
                {info.billingTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    role="radio"
                    aria-checked={type.value === billingType}
                    onClick={() => setBillingType(type.value)}
                    className={chipClass(type.value === billingType)}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-slate-500">{BILLING_HINTS[billingType]}</p>
            </fieldset>

            {needsDocument ? (
              <div>
                <label htmlFor="subscription-document" className={labelClass}>
                  CPF ou CNPJ de quem paga
                </label>
                <input
                  id="subscription-document"
                  inputMode="numeric"
                  value={documentId}
                  onChange={(event) => setDocumentId(event.target.value)}
                  className={fieldClass}
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Esta conta não tem documento cadastrado, e o Asaas exige para emitir a cobrança.
                </p>
              </div>
            ) : null}

            <div>
              <label htmlFor="subscription-note" className={labelClass}>
                Observação interna
              </label>
              <input
                id="subscription-note"
                maxLength={500}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Ex.: fechado no evento, 20% por 12 meses"
                className={fieldClass}
              />
            </div>

            {canApplyNow && planDiffers ? (
              <label className="flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={applyNow}
                  onChange={(event) => setApplyNow(event.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-semibold">Aplicar produto e profissionais na conta agora.</span>{" "}
                  Sem marcar, a conta só muda quando o próximo pagamento entrar.
                </span>
              </label>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
};

export default SubscriptionFormModal;
